/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: Presentation
 * Reason: A-2 — the „Verlauf“ tab of the signing-process OBC. Renders the six-source merge of
 *         {@link SmSigningProcessTimelineService} as a PrimeNG p-timeline with per-signer lanes and
 *         a separate treatment for the rows the backend itself declares as needing attention.
 *         Read-only: it opens records, it never changes one.
 */
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {
    MvsObjectNavigationEntry
} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {
    MvsObjectNavigationService
} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {SmTimelineEntryDto} from '../../model/dto/api/sm-timeline-entry.dto';
import {SmTimelineLaneDto} from '../../model/dto/api/sm-timeline-lane.dto';
import {
    SmSigningProcessTimelineService,
    SM_TIMELINE_EMPTY,
    SM_TIMELINE_ERROR,
    SM_TIMELINE_HEADING,
    SM_TIMELINE_LOADING
} from '../../service/domain/sm-signing-process-timeline/sm-signing-process-timeline.service';

/**
 * The colour stems a signer LANE is drawn in, in assignment order.
 *
 * <h2>Why this is not the forbidden colour table</h2>
 * The binding constraint of the signed design forbids a hand-written TypeScript table that maps an
 * ENUM MEMBER to a colour, an icon or a label — because the backend already declares exactly that
 * through {@code @UiEnumValueInfo} and a second table drifts from it silently. This array maps
 * nothing of the kind. Its key is the POSITION of a signer in a runtime list of rows, and no backend
 * metadata exists, or could exist, for „which colour is the second signer of this process“: the
 * signers are data, not enum members, and the same person is the first lane in one process and the
 * third in another.
 *
 * The single presentation fact that IS declared for a signer — the German label and colour of
 * {@code signerStatus} — is read from {@link SmTimelineLaneDto.signerStatusValueListEntry} and never
 * from here.
 *
 * Stems only. The template composes {@code text-…-800}, {@code bg-…-100} and {@code border-…-300}
 * from them, so a lane is one decision rather than three that can disagree. Every value is a
 * PrimeFlex colour token; no hex or rgb appears in this component.
 */
export const SM_TIMELINE_LANE_COLOURS: readonly string[] = [
    'indigo', 'teal', 'purple', 'cyan', 'pink', 'bluegray'
];

/**
 * The colour stem of a row that belongs to the PROCESS rather than to one person, and the fallback
 * of a lane whose signer is no longer in the process's signer set.
 */
export const SM_TIMELINE_PROCESS_LANE_COLOUR = 'gray';

/** The lane label of a row that concerns the process rather than one person. */
export const SM_TIMELINE_PROCESS_LANE_LABEL = 'Vorgang';

/**
 * The colour stems the backend uses to declare „a reader has to look at this“.
 *
 * <h2>Read this before changing it — it is the reason there is no event-type table here</h2>
 * The Verlauf has to lift six named conditions out of the stream: {@code dispatchRefused},
 * {@code dispatchRetryFailed}, {@code otpLocked}, {@code signerDeclined},
 * {@code auditTrailNotProduced}, {@code originNotCheckable}, plus the recipient states
 * {@code MISSING_CONTACT}, {@code MISSING_APPROVAL} and {@code ERROR}. The obvious implementation is
 * a set of those nine names in TypeScript — and that is precisely the hand-written enum table the
 * design forbids, in the shape that hides best.
 *
 * It is also unnecessary, which was verified against the two enums rather than assumed:
 * {@code SmAuditEventTypeEnum} declares {@code red-200/red-800} for {@code dispatchRefused},
 * {@code signerDeclined}, {@code otpLocked} and {@code auditTrailNotProduced} and
 * {@code orange-200/orange-800} for {@code dispatchRetryFailed} and {@code originNotCheckable};
 * {@code NsNotificationChannelRecipientStatusEnum} declares {@code orange} for
 * {@code MISSING_CONTACT} and {@code red} for {@code MISSING_APPROVAL} and {@code ERROR}. All nine
 * are therefore already marked by the backend, and this component asks the ONE question the backend
 * answers — „did you declare this row red or orange?“ — instead of restating the list.
 *
 * The rule deliberately reaches further than the nine: {@code SmAuditEventTypeEnum} declares thirteen
 * red-or-orange members, so a cancelled process, an expired process, a revoked link, a withdrawn
 * identification and the two OTP test-channel warnings are lifted out as well. That is the point. A
 * hand-kept list of nine would have shown a development OTP code as an ordinary row, and it would
 * have gone on doing so silently after the next enum member was appended.
 */
const SM_TIMELINE_ATTENTION_COLOURS: readonly string[] = ['red', 'orange'];

/**
 * The „Verlauf“ of one signing process.
 *
 * <h2>Its own p-timeline, and why not the platform's timeline widget</h2>
 * {@code MvsWidgetTimelineComponent} extends {@code MvsWidgetListComponent}: one {@code DtoList},
 * one {@code dataProviderObject} for every row and one fixed marker icon. It is row-HOMOGENEOUS by
 * construction — every row is the same entity — and this surface is the opposite: five entity types
 * in one track, a per-signer lane, and a per-row marker that has to come from the row's own backend
 * metadata. Signed as JUSTIFIED-CUSTOM (FD-4) against the in-tree precedents
 * {@code wf-process-timeline}, {@code cw-case-history-section} and {@code ch-timeline-feed}.
 *
 * <h2>Where every colour, icon and word on this surface comes from</h2>
 * <ul>
 *   <li><b>The marker</b> — icon, foreground and background — is
 *       {@link SmTimelineEntryDto.valueListEntry}, i.e. the {@code @UiEnumValueInfo} the BACKEND
 *       declares for the enum column of the row's source. There is no icon table and no colour table
 *       for an event type in this file. A row whose source column carries no value list gets a
 *       neutral bubble with no icon, which is what {@link SmTimelineEntryDto.valueListEntry} demands
 *       — the gap is never filled from a local map.</li>
 *   <li><b>The title and the detail line</b> are what the service put on the row; this component
 *       writes no German for a record it did not read.</li>
 *   <li><b>The lane</b> is the one thing that has no backend declaration and cannot have one — see
 *       {@link SM_TIMELINE_LANE_COLOURS}.</li>
 * </ul>
 *
 * <h2>The lane is a colour AND a word</h2>
 * Every row carries the lane's NAME as a chip, not only its colour. A lane told apart by hue alone is
 * unreadable for a red-green colour blind caseworker and invisible in a printed or greyscale copy of
 * the screen, which is the form this history is most likely to be read in when it is read at all.
 *
 * <h2>Read-only</h2>
 * One affordance per row: open the record behind it. Dispatching, reissuing a link and cancelling
 * stay where they were — this surface is what a caseworker reads to reconstruct what happened, and a
 * screen that both explains the past and changes it invites a change made while reading.
 *
 * <h2>The authentication method is not a legal quality</h2>
 * Signature rows carry the authentication method as the service labelled it. There is no persisted
 * evidence level anywhere in the module: {@code SmEvidenceLevelEnum} is derived at runtime by
 * {@code SmSignatureEvidenceService#deriveEvidenceLevel}, and the only stored column of that type is
 * {@code SmSignatureProfile.inPersonEvidenceLevel}, which is configuration. This component therefore
 * neither computes nor renames it — presenting the derivation's INPUT as its outcome would state a
 * legal quality the data does not carry.
 *
 * <h2>Scale</h2>
 * No volume is claimed (FD-5). It renders the whole history of one process unpaged, which fits the
 * order of magnitude a signing process produces and says nothing about a process with hundreds of
 * events.
 */
@Component({
    selector: 'mvs-sm-signing-process-timeline',
    templateUrl: './sm-signing-process-timeline.component.html',
    styleUrls: ['./sm-signing-process-timeline.component.scss'],
    standalone: false
})
export class SmSigningProcessTimelineComponent implements OnInit, OnChanges, OnDestroy {

    /** The `sm.SmSigningProcess` whose history is shown. */
    @Input() signingProcessId: number;

    /** Heading, loading, empty and error text — the wording is the service's, never re-invented. */
    readonly heading: string = SM_TIMELINE_HEADING;
    readonly loadingText: string = SM_TIMELINE_LOADING;
    readonly emptyText: string = SM_TIMELINE_EMPTY;
    readonly errorText: string = SM_TIMELINE_ERROR;

    /**
     * The four states this surface can be in, all of them rendered.
     *
     * {@code empty} is separated from {@code ready} rather than expressed as „ready with no rows“:
     * an empty history and a failed read look identical on a surface that only distinguishes „has
     * rows“ from „has none“, and telling a caseworker that nothing happened when the request failed
     * is the one wrong answer here.
     */
    state: 'loading' | 'ready' | 'empty' | 'error' = 'loading';

    /** The signer lanes, ascending by signing order. */
    lanes: SmTimelineLaneDto[] = [];

    /** Every row, newest first, as the service sorted them. */
    entries: SmTimelineEntryDto[] = [];

    /** Lane position by signer id — filled once per load, so the template lookups stay O(1). */
    private laneIndexBySignerId: Map<number, number> = new Map<number, number>();

    /** Lane by signer id, for the chip caption and the status entry. */
    private laneBySignerId: Map<number, SmTimelineLaneDto> = new Map<number, SmTimelineLaneDto>();

    private loadSubscription: Subscription;

    constructor(
        protected timelineService: SmSigningProcessTimelineService,
        protected navigationService: MvsObjectNavigationService) {
    }

    ngOnInit(): void {
        this.load();
    }

    /**
     * Reloads when the process changes.
     *
     * Guarded on the id having actually moved: the host re-renders this component on every tab
     * switch and on every {@code refreshObject}, and reloading six queries because an unrelated input
     * was re-assigned would put the surface back into its loading state for no reason.
     */
    ngOnChanges(changes: SimpleChanges): void {

        const change = changes['signingProcessId'];

        if (change && !change.firstChange && change.currentValue !== change.previousValue) {
            this.load();
        }
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
    }

    /**
     * Reads the whole history in one round trip.
     *
     * The error path leaves the previously shown rows in place behind the error message rather than
     * blanking them: a failed refresh must not read as „the history is now empty“.
     */
    load(): void {

        if (this.signingProcessId == null) {
            this.state = 'empty';
            return;
        }

        this.state = 'loading';
        this.loadSubscription?.unsubscribe();

        this.loadSubscription = this.timelineService.load(this.signingProcessId).subscribe({
            next: (timeline) => {

                this.lanes = timeline?.lanes ?? [];
                this.entries = timeline?.entries ?? [];

                this.laneIndexBySignerId = new Map<number, number>();
                this.laneBySignerId = new Map<number, SmTimelineLaneDto>();

                this.lanes.forEach((lane, index) => {
                    this.laneIndexBySignerId.set(lane.signerId, index);
                    this.laneBySignerId.set(lane.signerId, lane);
                });

                this.state = this.entries.length > 0 ? 'ready' : 'empty';
            },
            error: () => {
                this.state = 'error';
            }
        });
    }

    // =================================================================================================
    // The lane of a row
    // =================================================================================================

    /**
     * The lane caption of a row: the signing person, or the process itself.
     *
     * Falls back through the signer's ROLE name before it falls back to the bare reference, because
     * {@code signerName} is legitimately empty for a signer whose person was never captured — the
     * role („Antragsteller“) is then the only word that identifies the lane, and a reference number
     * identifies nothing to a reader.
     */
    laneLabel(entry: SmTimelineEntryDto): string {

        if (entry?.signerId == null) {
            return SM_TIMELINE_PROCESS_LANE_LABEL;
        }

        const lane = this.laneBySignerId.get(entry.signerId);

        if (!lane) {
            // A row anchored on a signer the process no longer lists. Naming the reference is the
            // honest answer; inventing a name for it would not be.
            return 'Unterzeichner #' + entry.signerId;
        }

        return lane.signerName || lane.signerRole || ('Unterzeichner #' + lane.signerId);
    }

    /** The colour stem of a row's lane. See {@link SM_TIMELINE_LANE_COLOURS}. */
    laneColour(entry: SmTimelineEntryDto): string {
        return this.laneColourOf(entry?.signerId ?? null);
    }

    /** The colour stem of one lane, addressed by its signer id. */
    laneColourOf(signerId: number | null): string {

        if (signerId == null) {
            return SM_TIMELINE_PROCESS_LANE_COLOUR;
        }

        const index = this.laneIndexBySignerId.get(signerId);

        if (index == null) {
            return SM_TIMELINE_PROCESS_LANE_COLOUR;
        }

        // Wraps rather than running out: a process with more signers than stems repeats a hue, and a
        // repeated hue next to a DIFFERENT name is still readable — a lane with no colour at all is
        // not.
        return SM_TIMELINE_LANE_COLOURS[index % SM_TIMELINE_LANE_COLOURS.length];
    }

    /** The lane chip's classes, addressed by signer id — used by the lane legend above the track. */
    laneChipClassOf(signerId: number | null): string {
        const colour = this.laneColourOf(signerId);
        return 'text-' + colour + '-800 bg-' + colour + '-100';
    }

    /** The lane chip's classes for one row. */
    laneChipClass(entry: SmTimelineEntryDto): string {
        return this.laneChipClassOf(entry?.signerId ?? null);
    }

    /** The card's left edge, which carries the lane colour down the whole row. */
    laneBorderClass(entry: SmTimelineEntryDto): string {
        return 'border-' + this.laneColour(entry) + '-300';
    }

    /** The lane caption of one lane, addressed by the lane itself — used by the legend. */
    laneCaption(lane: SmTimelineLaneDto): string {
        return lane.signerName || lane.signerRole || ('Unterzeichner #' + lane.signerId);
    }

    // =================================================================================================
    // The marker — the backend's own presentation of the row
    // =================================================================================================

    /** The Font Awesome class the backend declared, or {@code null} for a neutral bubble. */
    markerIcon(entry: SmTimelineEntryDto): string {
        return entry?.valueListEntry?.image || null;
    }

    /**
     * The bubble's classes, from the backend's declared colours.
     *
     * A row without a value list gets the neutral surface treatment. It is deliberately NOT filled in
     * from a local table — see the class comment.
     */
    markerClass(entry: SmTimelineEntryDto): string {

        const meta = entry?.valueListEntry;

        if (!meta?.backgroundColor && !meta?.color) {
            return 'surface-200 text-color-secondary';
        }

        const parts: string[] = [];

        if (meta.backgroundColor) {
            parts.push('bg-' + meta.backgroundColor);
        }

        if (meta.color) {
            parts.push('text-' + meta.color);
        }

        return parts.join(' ');
    }

    // =================================================================================================
    // Rows that need a reader's attention
    // =================================================================================================

    /**
     * Whether the row is one the backend marked as needing attention.
     *
     * Two independent sources, and both are facts rather than a table:
     * <ol>
     *   <li>{@link SmTimelineEntryDto.severity}, which the service derives from something the ROW
     *       carries and that has no presentation metadata of its own — a delivery that recorded an
     *       error message, a signature field that carries a signing moment but is not signed.</li>
     *   <li>The colour family the BACKEND declared for the row's enum value. See
     *       {@code SM_TIMELINE_ATTENTION_COLOURS} for why this replaces a list of event-type
     *       names and which nine conditions it was verified against.</li>
     * </ol>
     */
    isException(entry: SmTimelineEntryDto): boolean {

        if (entry?.severity === 'danger' || entry?.severity === 'warn') {
            return true;
        }

        return SmSigningProcessTimelineComponent.isAttentionColour(entry?.valueListEntry?.backgroundColor)
            || SmSigningProcessTimelineComponent.isAttentionColour(entry?.valueListEntry?.color);
    }

    /**
     * Whether a PrimeFlex colour token belongs to the attention family.
     *
     * Compares the STEM, so {@code red-200} and {@code red-800} are one answer and a future
     * {@code red-500} needs no edit here.
     */
    private static isAttentionColour(token: string | undefined): boolean {

        if (!token) {
            return false;
        }

        return SM_TIMELINE_ATTENTION_COLOURS.indexOf(token.split('-')[0]) >= 0;
    }

    // =================================================================================================
    // Opening the record behind a row
    // =================================================================================================

    /**
     * Opens the record a row was built from, in the side panel.
     *
     * The object type travels as its {@code cc.ObjectType} ALIAS, which is what the service put on the
     * row. The numeric object type id is a per-tenant-database fact and is never used to address an
     * object on this path.
     *
     * Opened beside the process rather than navigated to: the reader is reconstructing a sequence and
     * needs the row they came from to still be on screen when the record is closed again.
     */
    openSource(entry: SmTimelineEntryDto): void {

        if (!entry?.targetObjectAlias || entry.targetObjectId == null) {
            return;
        }

        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            entry.targetObjectAlias,
            entry.targetObjectId,
            null,
            entry.title,
            null,
            null,
            MvsObjectNavigationActionEnum.any);

        this.navigationService.navigateTo(navigationEntry, 'right');
    }
}
